import type { FC, SVGProps } from 'react';
import type { UnitHeaderBadgeColors } from '../../../businessLogicService/StudentDashboardAux/getColorByUnit';

interface Props extends SVGProps<SVGSVGElement> {
  colors: UnitHeaderBadgeColors;
  // When muted, only the unit-colored chrome (ring/highlight/bubble outline) is desaturated; the
  // inner multicolor checked-card icon keeps its color.
  muted?: boolean;
}

// The Exit Slip badge: a circular disc with a checked-card icon and a count bubble at the bottom.
// The ring, top shine, and bubble outline take the selected unit's colors; the inner icon keeps its
// own blue/green fills. The count number is not drawn here — UnitSkillBadge overlays the real count.
export const UnitHeaderExitSlipIcon: FC<Props> = ({ colors, muted, ...svgProps }) => {
  const mutedClass = muted ? ' tw-grayscale' : '';
  return (
    <svg viewBox="0 0 55 66" fill="none" xmlns="http://www.w3.org/2000/svg" {...svgProps}>
      <path
        className={`${colors.ring}${mutedClass}`}
        d="M55 28C55 43.1878 42.6878 55.5 27.5 55.5C12.3122 55.5 0 43.1878 0 28C0 12.8122 12.3122 0.5 27.5 0.5C42.6878 0.5 55 12.8122 55 28Z"
      />
      <path
        className={`${colors.highlight}${mutedClass}`}
        d="M46.5385 8.008C37.8859 -0.156633 24.4671 -2.10522 13.7159 4.3649C1.85384 11.5036 -3.27237 25.414 2.17626 38.5769L46.5385 8.008Z"
      />
      <path
        d="M47.5966 28C47.5966 39.0988 38.5992 48.0961 27.5005 48.0961C16.4017 48.0961 7.4043 39.0988 7.4043 28C7.4043 16.9012 16.4017 7.90381 27.5005 7.90381C38.5992 7.90381 47.5966 16.9012 47.5966 28Z"
        fill="#FEFBF9"
      />
      {/* The artwork is authored on its own 0..27 grid; translate it so the blue card's center
          (14, 14) lands on the disc's center (27.5, 28). */}
      <g transform="translate(13.5 14)">
        <rect x="4" y="4" width="20" height="20" rx="2" fill="#B7E2FF" />
        <path
          d="M8.40039 9.91704C8.40039 9.10315 9.02719 8.44336 9.80039 8.44336H18.2004C18.9736 8.44336 19.6004 9.10315 19.6004 9.91704V18.7591C19.6004 19.573 18.9736 20.2328 18.2004 20.2328H9.80039C9.02719 20.2328 8.40039 19.573 8.40039 18.7591V9.91704Z"
          fill="white"
        />
        <path
          d="M21.4168 7.10645C22.0731 6.37733 23.1953 6.31836 23.9244 6.97461C24.6536 7.6309 24.7125 8.7531 24.0563 9.48227L16.3834 18.0086C16.0539 18.3747 15.5867 18.5882 15.0942 18.597C14.6019 18.6057 14.1278 18.4097 13.7856 18.0558L10.0914 14.2325C9.40962 13.5272 9.42768 12.4025 10.133 11.7207C10.8383 11.039 11.963 11.0587 12.6448 11.7637L15.0165 14.2159L21.4168 7.10645Z"
          fill="#42C5A6"
        />
      </g>
      <mask
        id="unit-header-exit-slip-bubble-outline"
        maskUnits="userSpaceOnUse"
        x="12"
        y="42"
        width="32"
        height="24"
        fill="black"
      >
        <rect fill="white" x="12" y="42" width="32" height="24" />
        <path d="M14 50C14 46.6863 16.6863 44 20 44H36C39.3137 44 42 46.6863 42 50V56C42 59.3137 39.3137 62 36 62H20C16.6863 62 14 59.3137 14 56V50Z" />
      </mask>
      <path
        d="M14 50C14 46.6863 16.6863 44 20 44H36C39.3137 44 42 46.6863 42 50V56C42 59.3137 39.3137 62 36 62H20C16.6863 62 14 59.3137 14 56V50Z"
        fill="#FEFBF9"
      />
      <path
        className={`${colors.bubbleBorder}${mutedClass}`}
        d="M12 50C12 45.5817 15.5817 42 20 42H36C40.4183 42 44 45.5817 44 50H40C40 47.7909 38.2091 46 36 46H20C17.7909 46 16 47.7909 16 50H12ZM44 58C44 62.4183 40.4183 66 36 66H20C15.5817 66 12 62.4183 12 58L16 56C16 57.1046 17.7909 58 20 58H36C38.2091 58 40 57.1046 40 56L44 58ZM20 66C15.5817 66 12 62.4183 12 58V50C12 45.5817 15.5817 42 20 42V46C17.7909 46 16 47.7909 16 50V56C16 57.1046 17.7909 58 20 58V66ZM36 42C40.4183 42 44 45.5817 44 50V58C44 62.4183 40.4183 66 36 66V58C38.2091 58 40 57.1046 40 56V50C40 47.7909 38.2091 46 36 46V42Z"
        mask="url(#unit-header-exit-slip-bubble-outline)"
      />
    </svg>
  );
};
